import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { colors } from 'styles/colors';
import { getInvitation, acceptInvitation } from 'api/invitations';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { FormattedMessage } from 'react-intl';
import { useSignOut } from 'react-auth-kit';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { CalendarDays, Clock, MapPin, Star } from 'lucide-react';

interface Props {
  invitationId: string;
}

const InfoCard = ({
  icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: React.ReactNode;
  value: React.ReactNode;
}) => {
  const IconComponent = icon;
  return (
    <Flex
      align='center'
      justify='space-between'
      px={4}
      py={3}
      borderWidth='1px'
      borderColor='dark.D150'
      borderRadius='12px'
      bg='white'
    >
      <Flex direction='column' align='flex-end' textAlign='right' mx={3} flex={1} gap={0.5}>
        <Text textStyle='bodySmallRegular' color='dark.D500'>
          {label}
        </Text>
        <Text textStyle='bodySmallMedium' color='dark.D900'>
          {value}
        </Text>
      </Flex>
      <Box bg={colors.BG100} p={2} borderRadius='10px'>
        <IconComponent size={20} color={colors.b2b.landingHeroCta} strokeWidth={1.8} />
      </Box>
    </Flex>
  );
};

export const InvitationDetails = ({ invitationId }: Props) => {
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery(
    `invitation-${invitationId}`,
    () => getInvitation(invitationId),
    { retry: false },
  );

  useEffect(() => {
    if (data?.invitationStatus === 'ACCEPTED' && data.ticket) {
      navigate(`/${ROUTES.ticketGenerator.replace(':ticketId', data.ticket.id)}`);
    }
  }, [data, navigate]);

  const handleAccept = async () => {
    setIsAccepting(true);
    setAcceptError(null);
    try {
      const response = await acceptInvitation(invitationId);
      if (response.ticket) {
        navigate(`/${ROUTES.ticketGenerator.replace(':ticketId', response.ticket.id)}`);
      }
    } catch (e) {
      const message =
        axios.isAxiosError(e) && typeof e.response?.data?.message === 'string'
          ? e.response.data.message
          : 'حدث خطأ، يرجى المحاولة مجدداً';
      setAcceptError(message);
    } finally {
      setIsAccepting(false);
    }
  };

  if (isFetching) return <LoaderOverlay />;

  if (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;

    if (status === 403) {
      return (
        <Flex
          direction='column'
          flex={1}
          align='center'
          justify='center'
          px={6}
          py={12}
          textAlign='right'
          gap={6}
        >
          <Text textStyle='headlineMedium' color='dark.D900'>
            <FormattedMessage
              id='invitation.error.wrongPhone'
              defaultMessage='هذا الرقم غير مدعو لهذا الحدث'
            />
          </Text>
          <Text textStyle='bodySmallRegular' color='dark.D500'>
            <FormattedMessage
              id='invitation.error.wrongPhoneHint'
              defaultMessage='تأكد أن الرقم المسجل هو نفس الرقم الذي وصلتك عليه الدعوة.'
            />
          </Text>
          <Button
            w='100%'
            textAlign='center'
            bg={colors.b2b.accent}
            color='white'
            _hover={{ bg: colors.b2b.accentHover }}
            _active={{ bg: colors.b2b.accentActive }}
            borderRadius='full'
            size='lg'
            onClick={() => signOut()}
          >
            <FormattedMessage
              id='invitation.error.signOutAndRetry'
              defaultMessage='تسجيل الخروج والمحاولة برقم آخر'
            />
          </Button>
        </Flex>
      );
    }

    if (status === 404) {
      return (
        <Flex
          direction='column'
          flex={1}
          align='center'
          justify='center'
          px={6}
          py={12}
          textAlign='right'
        >
          <Text textStyle='headlineMedium' color='dark.D900'>
            <FormattedMessage id='invitation.error.notFound' defaultMessage='الدعوة غير موجودة' />
          </Text>
        </Flex>
      );
    }

    return (
      <Flex
        direction='column'
        flex={1}
        align='center'
        justify='center'
        px={6}
        py={12}
        textAlign='right'
      >
        <Text textStyle='headlineMedium' color='dark.D900'>
          <FormattedMessage id='global.error.generictitle' defaultMessage='حدث خطأ' />
        </Text>
      </Flex>
    );
  }

  if (!data) return null;

  const eventInfo = data.eventInfo;
  const inviteeName = data.invitationDetails?.name;

  return (
    <Flex direction='column' flex={1} bg='white'>
      {/* Header: greeting + invitee name + private badge */}
      <Flex justify='space-between' align='flex-start' px={4} pt={5} pb={4}>
        <Flex
          align='center'
          gap='4px'
          px='10px'
          py='4px'
          borderRadius='999px'
          border={`1px solid ${colors.b2b.inviteAccentLight}`}
          bgGradient={`linear(90deg, ${colors.b2b.inviteGradientFrom} 0%, ${colors.b2b.inviteGradientTo} 100%)`}
          w='93px'
          h='25px'
        >
          <Star size={13} color={colors.b2b.accent} />
          <Text textStyle='bodySmallRegular' color={colors.b2b.accent} fontSize='xs'>
            <FormattedMessage id='invitation.details.privateLabel' defaultMessage='دعوة خاصة' />
          </Text>
        </Flex>
        <Flex direction='column' align='flex-end'>
          <Text textStyle='bodySmallRegular' color='dark.D500'>
            <FormattedMessage id='invitation.details.greeting' defaultMessage='يسرّنا دعوة' />
          </Text>
          <Text fontSize='xl' fontWeight='bold' color={inviteeName ? 'dark.D900' : 'dark.D300'}>
            {inviteeName ?? '—'}
          </Text>
        </Flex>
      </Flex>

      {/* Event image */}
      <Box w='100%' sx={{ aspectRatio: '16/9' }}>
        <Image
          src={eventInfo?.imageLink ?? 'https://placehold.co/600x400?text=Hello+World'}
          w='100%'
          h='100%'
          objectFit='cover'
        />
      </Box>

      {/* Content */}
      <Flex direction='column' flex={1} px={4} pt={5} pb={8} gap={4}>
        {/* Info cards */}
        {eventInfo && (
          <Flex direction='column' gap={3}>
            {eventInfo.date && (
              <InfoCard
                icon={CalendarDays}
                label={<FormattedMessage id='invitation.details.date' defaultMessage='التاريخ' />}
                value={eventInfo.date}
              />
            )}
            {eventInfo.time && (
              <InfoCard
                icon={Clock}
                label={<FormattedMessage id='invitation.details.time' defaultMessage='وقت الحدث' />}
                value={eventInfo.time}
              />
            )}
            {(eventInfo.city || eventInfo.stadium) && (
              <InfoCard
                icon={MapPin}
                label={
                  <FormattedMessage id='invitation.details.location' defaultMessage='المكان' />
                }
                value={[eventInfo.stadium, eventInfo.city].filter(Boolean).join('، ')}
              />
            )}
          </Flex>
        )}

        {acceptError && (
          <Text textStyle='bodySmallMedium' color='red' textAlign='right'>
            {acceptError}
          </Text>
        )}
      </Flex>

      {/* Accept button */}
      <Box px={4} pb={8}>
        <Button
          w='100%'
          isLoading={isAccepting}
          onClick={handleAccept}
          bg={colors.b2b.accent}
          color='white'
          _hover={{ bg: colors.b2b.accentHover }}
          _active={{ bg: colors.b2b.accentActive }}
          borderRadius='full'
          size='lg'
        >
          <FormattedMessage id='invitation.details.accept' defaultMessage='قبول الدعوة' />
        </Button>
      </Box>
    </Flex>
  );
};
